import { LiveAnnouncer } from '@angular/cdk/a11y';
import { Injectable, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';

export const BASE_TITLE = 'ACHIEVE';

/**
 * Owns `document.title` and announces page changes to screen readers.
 * Bugs 33081 (WCAG 2.4.2 Page Titled) and 33082 (WCAG 4.1.3 Status Messages).
 *
 * The only caller is AchieveTitleStrategy, which the Router invokes once per navigation. Nothing
 * else writes the title — in particular the page header does not, so no on-screen heading can reach
 * the browser tab. Headings routinely include a learner's name and document.title propagates to
 * browser history, window titles and anything screen-shared.
 *
 * Deliberately does not inject Router. The Router builds TitleStrategy as part of its own
 * construction, so depending on it here would be a circular dependency (NG0200 at bootstrap).
 * Counting updateTitle calls gives us everything we need without it.
 */
@Injectable({ providedIn: 'root' })
export class PageTitleService {
  private readonly title = inject(Title);
  private readonly liveAnnouncer = inject(LiveAnnouncer);

  /** Navigations seen so far. The first is the initial page load. */
  private navigationCount = 0;

  setTitle(pageTitle: string): void {
    this.navigationCount++;
    this.title.setTitle(this.format(pageTitle));

    // The browser and the screen reader both read the title natively on the first load; announcing
    // it again would stutter. Only subsequent route changes get announced.
    if (this.navigationCount < 2) {
      return;
    }

    // The page name alone — repeating "ACHIEVE" on every navigation is noise.
    this.liveAnnouncer.announce(pageTitle?.trim() || BASE_TITLE, 'polite');
  }

  /** Formats a page name the way the tab should read, e.g. `Learner Management - ACHIEVE`. */
  format(pageTitle: string): string {
    const trimmed = pageTitle?.trim();
    return trimmed ? `${trimmed} - ${BASE_TITLE}` : BASE_TITLE;
  }
}
